import React, { useState } from "react";
import { Show } from "@refinedev/antd";
import { useShow, useList, useNavigation, useCreate, useUpdate, useDelete, HttpError } from "@refinedev/core";
import {
  Card,
  Row,
  Col,
  List,
  Tag,
  Button,
  Space,
  Progress,
  Typography,
  Descriptions,
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  message,
  Tooltip,
  Badge,
  Empty,
  Divider,
  Timeline,
  Avatar,
  Collapse,
  Alert,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  MessageOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  CopyOutlined,
  MailOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { ITaskList, ITask, IComment, IAdvert } from "../../interfaces";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

export const TaskListShow: React.FC = () => {
  const { queryResult } = useShow<ITaskList>();
  const { data, isLoading, refetch } = queryResult;
  const taskList = data?.data;
  const { push } = useNavigation();

  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<ITask | null>(null);
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [commentingTask, setCommentingTask] = useState<ITask | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [copiedToken, setCopiedToken] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [taskForm] = Form.useForm();
  const [commentForm] = Form.useForm();

  // Mutations
  const { mutate: createTask } = useCreate();
  const { mutate: updateTask } = useUpdate();
  const { mutate: deleteTask } = useDelete();
  const { mutate: createComment } = useCreate();

  // Load advert data
  const { data: advertsData } = useList<IAdvert>({
    resource: "senders",
    pagination: { pageSize: 100 },
  });
  const adverts = advertsData?.data || [];

  // Task Status ändern
  const handleTaskStatusChange = (task: ITask, newStatus: string) => {
    updateTask(
      {
        resource: "tasks",
        id: task.id,
        values: { status: newStatus },
      },
      {
        onSuccess: () => {
          message.success("Status erfolgreich aktualisiert");
          refetch(); // Refetch the task list to update the task count
        },
      }
    );
  };

  // Task erstellen/bearbeiten
  const handleTaskSubmit = (values: any) => {
    if (editingTask) {
      updateTask(
        {
          resource: "tasks",
          id: editingTask.id,
          values,
        },
        {
          onSuccess: () => {
            message.success("Aufgabe erfolgreich aktualisiert");
            setIsTaskModalVisible(false);
            setEditingTask(null);
            taskForm.resetFields();
            refetch(); // Refetch the task list to show new task
          },
        }
      );
    } else {
      createTask(
        {
          resource: "tasks",
          values: {
            ...values,
            tasklist: taskList?.id,
          },
        },
        {
          onSuccess: () => {
            message.success("Aufgabe erfolgreich erstellt");
            setIsTaskModalVisible(false);
            taskForm.resetFields();
            refetch(); // Refetch the task list to show new task
          },
        }
      );
    }
  };

  // Task löschen
  const handleTaskDelete = (taskId: number) => {
    deleteTask(
      {
        resource: "tasks",
        id: taskId,
      },
      {
        onSuccess: () => {
          message.success("Aufgabe erfolgreich gelöscht");
          refetch(); // Refetch the task list to update the task count
        },
      }
    );
  };

  // Kommentar hinzufügen
  const handleCommentSubmit = (values: any) => {
    createComment(
      {
        resource: "comments",
        values: {
          ...values,
          task: commentingTask?.id,
        },
      },
      {
        onSuccess: () => {
          message.success("Kommentar erfolgreich hinzugefügt");
          setIsCommentModalVisible(false);
          setCommentingTask(null);
          commentForm.resetFields();
          refetch(); // Refetch the task list to update the comment count
        },
      }
    );
  };

  // Mobile-Link kopieren
  const copyMobileLink = () => {
    if (taskList?.access_token) {
      const mobileUrl = `${window.location.origin}/?type=8847379250&token=${taskList.access_token}`;
      navigator.clipboard.writeText(mobileUrl).then(() => {
        setCopiedToken(true);
        message.success("Mobile-Link kopiert!");
        setTimeout(() => setCopiedToken(false), 3000);
      });
    }
  };

  // Mobile-Link per E-Mail senden
  const sendMobileLink = async () => {
    if (!taskList?.access_token || !taskList?.assigned_email) {
      message.error("Keine E-Mail-Adresse zugewiesen oder Token fehlt!");
      return;
    }

    setEmailSending(true);
    
    try {
      const baseUrl = `${window.location.origin}/?type=8847379249`;
      const response = await fetch(`${baseUrl}&tx_buchungsmanagerfrontend_tasklistapi[controller]=TaskListApi&tx_buchungsmanagerfrontend_tasklistapi[action]=sendMobileLink&tx_buchungsmanagerfrontend_tasklistapi[uid]=${taskList.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskListId: taskList.id,
          assignedEmail: taskList.assigned_email
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setEmailSent(true);
        message.success(`Mobile-Link erfolgreich an ${taskList.assigned_email} gesendet!`);
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        message.error(`Fehler beim E-Mail-Versand: ${data.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('E-Mail send error:', error);
      message.error('Fehler beim E-Mail-Versand');
    } finally {
      setEmailSending(false);
    }
  };

  // Status-Tag rendern
  const renderTaskStatus = (status: string) => {
    const statusConfig = {
      pending: { color: "default", icon: <ClockCircleOutlined />, text: "Ausstehend" },
      in_progress: { color: "processing", icon: <SyncOutlined spin />, text: "In Arbeit" },
      completed: { color: "success", icon: <CheckCircleOutlined />, text: "Erledigt" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Priorität rendern
  const renderPriority = (priority: string) => {
    const priorityConfig = {
      low: { color: "default", text: "Niedrig" },
      normal: { color: "blue", text: "Normal" },
      high: { color: "red", text: "Hoch", icon: <FlagOutlined /> },
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Kommentare rendern
  const renderComments = (comments: IComment[]) => {
    if (!comments || comments.length === 0) return null;

    return (
      <Timeline style={{ marginTop: 16 }}>
        {comments.map((comment) => (
          <Timeline.Item
            key={comment.id}
            color={comment.is_important ? "red" : "gray"}
            dot={comment.requires_response ? <ExclamationCircleOutlined /> : null}
          >
            <div>
              <Space>
                <Avatar icon={<UserOutlined />} size="small" />
                <Text strong>{comment.author_display}</Text>
                <Text type="secondary">{comment.created_formatted}</Text>
                {comment.is_important && <Tag color="red">Wichtig</Tag>}
                {comment.requires_response && <Tag color="orange">Antwort erforderlich</Tag>}
              </Space>
              <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                {comment.content}
              </Paragraph>
            </div>
          </Timeline.Item>
        ))}
      </Timeline>
    );
  };

  // Task-Karte rendern
  const renderTaskCard = (task: ITask) => {
    const isExpanded = expandedTasks.includes(task.id.toString());

    return (
      <Card
        key={task.id}
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: 16 }}
        actions={[
          <Tooltip title="Status ändern">
            <Checkbox
              checked={task.status === "completed"}
              onChange={(e) =>
                handleTaskStatusChange(
                  task,
                  e.target.checked ? "completed" : "pending"
                )
              }
            >
              Erledigt
            </Checkbox>
          </Tooltip>,
          <Button
            type="link"
            icon={<MessageOutlined />}
            onClick={() => {
              setCommentingTask(task);
              setIsCommentModalVisible(true);
            }}
          >
            Kommentar ({task.comment_count || 0})
          </Button>,
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingTask(task);
              taskForm.setFieldsValue(task);
              setIsTaskModalVisible(true);
            }}
          >
            Bearbeiten
          </Button>,
          <Popconfirm
            title="Aufgabe wirklich löschen?"
            onConfirm={() => handleTaskDelete(task.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Löschen
            </Button>
          </Popconfirm>,
        ]}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                <Title level={5} style={{ margin: 0 }}>
                  {task.title}
                </Title>
                <Space>
                  {renderPriority(task.priority)}
                  {renderTaskStatus(task.status)}
                </Space>
              </Space>

              {task.description && (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {task.description}
                </Paragraph>
              )}

              {task.assigned_to && (
                <Space>
                  <UserOutlined />
                  <Text>Zugewiesen an: {task.assigned_to}</Text>
                </Space>
              )}

              {task.status === "completed" && task.completed_at && (
                <Space>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  <Text type="secondary">
                    Erledigt am {new Date(task.completed_at * 1000).toLocaleString("de-DE")}
                    {task.completed_by && ` von ${task.completed_by}`}
                  </Text>
                </Space>
              )}
            </Space>
          </Col>

          {task.comments && task.comments.length > 0 && (
            <Col span={24}>
              <Collapse
                ghost
                activeKey={isExpanded ? ["comments"] : []}
                onChange={(keys) => {
                  setExpandedTasks(
                    keys.includes("comments")
                      ? [...expandedTasks, task.id.toString()]
                      : expandedTasks.filter((id) => id !== task.id.toString())
                  );
                }}
              >
                <Panel
                  header={
                    <Space>
                      <MessageOutlined />
                      {task.comments.length} Kommentar(e)
                    </Space>
                  }
                  key="comments"
                >
                  {renderComments(task.comments)}
                </Panel>
              </Collapse>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  if (!taskList) return null;

  const advert = adverts.find((a) => a.id === taskList.advert);

  return (
    <Show
      title={taskList.title}
      headerButtons={[
        <Button
          key="edit"
          type="primary"
          icon={<EditOutlined />}
          onClick={() => push(`/tasklists/bearbeiten/${taskList.id}`)}
        >
          Bearbeiten
        </Button>,
      ]}
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card title="Aufgaben" extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTask(null);
                taskForm.resetFields();
                setIsTaskModalVisible(true);
              }}
            >
              Neue Aufgabe
            </Button>
          }>
            {taskList.tasks && taskList.tasks.length > 0 ? (
              <List
                dataSource={taskList.tasks}
                renderItem={(task) => renderTaskCard(task)}
              />
            ) : (
              <Empty description="Noch keine Aufgaben vorhanden" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Card title="Details">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Status">
                  {taskList.status === "open" && <Tag color="blue">Offen</Tag>}
                  {taskList.status === "in_progress" && <Tag color="orange">In Arbeit</Tag>}
                  {taskList.status === "completed" && <Tag color="green">Erledigt</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Zugewiesen an">
                  <Space>
                    <MailOutlined />
                    {taskList.assigned_email}
                  </Space>
                </Descriptions.Item>
                {advert && (
                  <Descriptions.Item label="Objekt">
                    <Space>
                      <HomeOutlined />
                      {advert.title}
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Sichtbarkeit">
                  {taskList.visibility === "public" ? "Öffentlich" : "Intern"}
                </Descriptions.Item>
                <Descriptions.Item label="Erstellt">
                  {new Date(taskList.created * 1000).toLocaleDateString("de-DE")}
                </Descriptions.Item>
              </Descriptions>

              {taskList.description && (
                <>
                  <Divider />
                  <Title level={5}>Beschreibung</Title>
                  <Paragraph>{taskList.description}</Paragraph>
                </>
              )}
            </Card>

            <Card title="Mobile-Zugriff">
              <Alert
                message="Link für Reinigungskräfte"
                description="Teilen Sie diesen Link mit der zugewiesenen Person für mobilen Zugriff ohne Login."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Button
                block
                icon={copiedToken ? <CheckCircleOutlined /> : <CopyOutlined />}
                onClick={copyMobileLink}
                style={{ color: copiedToken ? "#52c41a" : undefined, marginBottom: 8 }}
              >
                {copiedToken ? "Link kopiert!" : "Mobile-Link kopieren"}
              </Button>
              
              <Button
                block
                type="primary"
                icon={emailSent ? <CheckCircleOutlined /> : <MailOutlined />}
                onClick={sendMobileLink}
                loading={emailSending}
                disabled={!taskList?.assigned_email}
                style={{ color: emailSent ? "#52c41a" : undefined }}
              >
                {emailSent ? "E-Mail gesendet!" : emailSending ? "Sende E-Mail..." : "Link per E-Mail senden"}
              </Button>
              
              {!taskList?.assigned_email && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                  Keine E-Mail-Adresse zugewiesen
                </Text>
              )}
              
              {taskList?.assigned_email && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                  Wird gesendet an: {taskList.assigned_email}
                </Text>
              )}
            </Card>

            {taskList.statistics && (
              <Card title="Fortschritt">
                <Progress
                  percent={taskList.statistics.completionRate || 0}
                  status={taskList.statistics.completionRate === 100 ? "success" : "active"}
                />
                <Space direction="vertical" style={{ width: "100%", marginTop: 16 }}>
                  <Text>Gesamt: {taskList.statistics.total}</Text>
                  <Text>Erledigt: {taskList.statistics.completed}</Text>
                  <Text>Ausstehend: {taskList.statistics.pending}</Text>
                  <Text>In Arbeit: {taskList.statistics.inProgress}</Text>
                </Space>
              </Card>
            )}
          </Space>
        </Col>
      </Row>

      {/* Task Modal */}
      <Modal
        title={editingTask ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
        open={isTaskModalVisible}
        onCancel={() => {
          setIsTaskModalVisible(false);
          setEditingTask(null);
          taskForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={taskForm}
          layout="vertical"
          onFinish={handleTaskSubmit}
        >
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: "Bitte Titel eingeben" }]}
          >
            <Input placeholder="z.B. Küche reinigen" />
          </Form.Item>

          <Form.Item name="description" label="Beschreibung">
            <TextArea rows={3} placeholder="Detaillierte Beschreibung..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Priorität" initialValue="normal">
                <Select>
                  <Select.Option value="low">Niedrig</Select.Option>
                  <Select.Option value="normal">Normal</Select.Option>
                  <Select.Option value="high">Hoch</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="assigned_to" label="Zugewiesen an">
                <Input placeholder="E-Mail-Adresse" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTask ? "Aktualisieren" : "Erstellen"}
              </Button>
              <Button onClick={() => setIsTaskModalVisible(false)}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Comment Modal */}
      <Modal
        title={`Kommentar zu: ${commentingTask?.title}`}
        open={isCommentModalVisible}
        onCancel={() => {
          setIsCommentModalVisible(false);
          setCommentingTask(null);
          commentForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={commentForm}
          layout="vertical"
          onFinish={handleCommentSubmit}
        >
          <Form.Item
            name="content"
            label="Kommentar"
            rules={[{ required: true, message: "Bitte Kommentar eingeben" }]}
          >
            <TextArea rows={4} placeholder="Ihr Kommentar..." />
          </Form.Item>

          <Form.Item name="author_name" label="Ihr Name">
            <Input placeholder="Optional" />
          </Form.Item>

          <Form.Item name="is_important" valuePropName="checked">
            <Checkbox>Als wichtig markieren</Checkbox>
          </Form.Item>

          <Form.Item name="requires_response" valuePropName="checked">
            <Checkbox>Antwort erforderlich</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Kommentar hinzufügen
              </Button>
              <Button onClick={() => setIsCommentModalVisible(false)}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  );
};